"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { UserRound, UsersRound } from "lucide-react";
import ArtistFollowButton from "@/features/artists/ArtistFollowButton";
import { avatarOf } from "@/features/home-v2/tiles";
import { DeskEmpty } from "@/features/home-v2/desk/Desk";
import { Avatar, Dialog, Segmented, Spinner } from "@/features/home-v2/ui";
import type { FollowListKind } from "./api";
import { useFollowList } from "./useFollowList";
import "./follow-lists.css";
type Props = {
  open: boolean;
  onClose: () => void;
  lang: "fa" | "en";
  username: string;
  kind: FollowListKind;
  onKind: (k: FollowListKind) => void;
};

/**
 * A profile's people — who follows them and whom they follow — in one dialog with the two as
 * tabs. Newest first, as the backend keeps them; more load as the list scrolls to its end.
 * Each row is the person (portrait, name, handle, their followers) and the follow button,
 * absent on the viewer's own row.
 */
export default function FollowListDialog(t0) {
  const $ = _c(56);
  const {
    open,
    onClose,
    lang,
    username,
    kind,
    onKind
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const {
    query,
    people,
    total,
    onFollowChange
  } = useFollowList(username, kind, open);
  const endRef = useRef(null);
  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = query;
  let t2;
  if ($[2] !== fetchNextPage || $[3] !== hasNextPage || $[4] !== isFetchingNextPage) {
    t2 = () => {
      const el = endRef.current;
      if (!el || !hasNextPage) {
        return;
      }
      const io = new IntersectionObserver(entries => {
        if (entries.some(_temp) && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, {
        rootMargin: "200px"
      });
      io.observe(el);
      return () => io.disconnect();
    };
    $[2] = fetchNextPage;
    $[3] = hasNextPage;
    $[4] = isFetchingNextPage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== fetchNextPage || $[7] !== hasNextPage || $[8] !== isFetchingNextPage || $[9] !== people.length) {
    t3 = [hasNextPage, isFetchingNextPage, fetchNextPage, people.length];
    $[6] = fetchNextPage;
    $[7] = hasNextPage;
    $[8] = isFetchingNextPage;
    $[9] = people.length;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] !== kind || $[12] !== t) {
    t4 = kind === "followers" ? t("\u062F\u0646\u0628\u0627\u0644\u200C\u06A9\u0646\u0646\u062F\u0647\u200C\u0647\u0627", "Followers") : t("\u062F\u0646\u0628\u0627\u0644\u200C\u0634\u062F\u0647\u200C\u0647\u0627", "Following");
    $[11] = kind;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const title = t4;
  const T0 = Dialog;
  let t5;
  if ($[14] !== t) {
    t5 = t("\u0628\u0633\u062A\u0646", "Close");
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t) {
    t6 = t("\u0641\u0647\u0631\u0633\u062A \u0627\u0641\u0631\u0627\u062F", "People");
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t) {
    t7 = t("\u062F\u0646\u0628\u0627\u0644\u200C\u06A9\u0646\u0646\u062F\u0647\u200C\u0647\u0627", "Followers");
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = {
      value: "followers",
      label: t7
    };
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t) {
    t9 = t("\u062F\u0646\u0628\u0627\u0644\u200C\u0634\u062F\u0647\u200C\u0647\u0627", "Following");
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = {
      value: "following",
      label: t9
    };
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t8) {
    t11 = [t8, t10];
    $[26] = t10;
    $[27] = t8;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== kind || $[30] !== onKind || $[31] !== t11 || $[32] !== t6) {
    t12 = <Segmented value={kind} onChange={onKind} label={t6} options={t11} />;
    $[29] = kind;
    $[30] = onKind;
    $[31] = t11;
    $[32] = t6;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = total != null ? <span className="fl-total">{t(`${nf.format(total)} نفر`, `${nf.format(total)} people`)}</span> : null;
  let t14;
  if ($[34] !== t12 || $[35] !== t13) {
    t14 = <div className="fl-head">{t12}{t13}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = query.isLoading ? <ul className="fl-list" aria-busy={true}>{Array.from({
      length: 6
    }, _temp2)}</ul> : query.isError && people.length === 0 ? <DeskEmpty icon={<UsersRound aria-hidden={true} />} tone="error" title={t("\u0641\u0647\u0631\u0633\u062A \u0628\u0627\u0632 \u0646\u0634\u062F", "The list could not be loaded")} body={t("\u0627\u062A\u0635\u0627\u0644 \u0631\u0627 \u0628\u0631\u0631\u0633\u06CC \u06A9\u0646\u06CC\u062F \u0648 \u062F\u0648\u0628\u0627\u0631\u0647 \u0627\u0645\u062A\u062D\u0627\u0646 \u06A9\u0646\u06CC\u062F.", "Check your connection and try again.")} actions={<button type="button" className="v2-btn v2-btn--ghost v2-btn--sm v2-focus" onClick={() => void query.refetch()}>{t("\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647", "Try again")}</button>} /> : people.length === 0 ? <DeskEmpty icon={<UserRound aria-hidden={true} />} title={kind === "followers" ? t("\u0647\u0646\u0648\u0632 \u06A9\u0633\u06CC \u062F\u0646\u0628\u0627\u0644\u0634 \u0646\u06A9\u0631\u062F\u0647", "No followers yet") : t("\u0647\u0646\u0648\u0632 \u06A9\u0633\u06CC \u0631\u0627 \u062F\u0646\u0628\u0627\u0644 \u0646\u06A9\u0631\u062F\u0647", "Not following anyone yet")} body={kind === "followers" ? t("\u062F\u0646\u0628\u0627\u0644\u200C\u06A9\u0646\u0646\u062F\u0647\u200C\u0647\u0627 \u0627\u06CC\u0646\u062C\u0627 \u0645\u06CC\u200C\u0622\u06CC\u0646\u062F.", "Followers will appear here.") : t("\u06A9\u0633\u0627\u0646\u06CC \u06A9\u0647 \u062F\u0646\u0628\u0627\u0644 \u0645\u06CC\u200C\u06A9\u0646\u062F \u0627\u06CC\u0646\u062C\u0627 \u0645\u06CC\u200C\u0622\u06CC\u0646\u062F.", "People they follow will appear here.")} /> : <ul className="fl-list">{people.map(p => {
      const name = p.name?.trim() || p.username;
      const followers = p.following?.follower ?? 0;
      const state = p.follow_state;
      return <li key={p.id} className="fl-row"><Link href={`/${lang}/artists/${encodeURIComponent(p.username)}`} className="fl-who v2-focus" onClick={onClose}><Avatar name={name} src={avatarOf(p)} size={40} /><span className="fl-id"><b>{name}</b><span className="fl-handle" dir="ltr">@{p.username}</span></span></Link><span className="fl-count">{t(`${nf.format(followers)} دنبال‌کننده`, `${nf.format(followers)} followers`)}</span>{state === "CAN_NOT_FOLLOW" ? <span className="fl-you">{t("\u062E\u0648\u062F\u062A\u0627\u0646", "You")}</span> : <ArtistFollowButton key={`${p.username}-${state}`} username={p.username} initialState={state === "FOLLOWED" ? "FOLLOWED" : "NOT_FOLLOWED"} isFa={isFa} onChange={next => onFollowChange(p.username, next)} />}</li>;
    })}</ul>;
  let t16;
  if ($[37] !== isFetchingNextPage) {
    t16 = isFetchingNextPage ? <Spinner /> : null;
    $[37] = isFetchingNextPage;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== fetchNextPage || $[40] !== people.length || $[41] !== query.isError || $[42] !== t) {
    t17 = query.isError && people.length > 0 ? <button type="button" className="v2-btn v2-btn--ghost v2-btn--sm v2-focus" onClick={() => void fetchNextPage()}>{t("\u0628\u0627\u0631\u06AF\u06CC\u0631\u06CC \u0627\u062F\u0627\u0645\u0647", "Load more")}</button> : null;
    $[39] = fetchNextPage;
    $[40] = people.length;
    $[41] = query.isError;
    $[42] = t;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t16 || $[45] !== t17) {
    t18 = <div ref={endRef} className="fl-end">{t16}{t17}</div>;
    $[44] = t16;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== T0 || $[48] !== onClose || $[49] !== open || $[50] !== t14 || $[51] !== t15 || $[52] !== t18 || $[53] !== t5 || $[54] !== title) {
    t19 = <T0 open={open} onClose={onClose} title={title} closeLabel={t5}>{t14}{t15}{t18}</T0>;
    $[47] = T0;
    $[48] = onClose;
    $[49] = open;
    $[50] = t14;
    $[51] = t15;
    $[52] = t18;
    $[53] = t5;
    $[54] = title;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  return t19;
}
function _temp2(_, i) {
  return <li key={i} className="fl-row fl-row--ghost" aria-hidden={true}><span /><span /></li>;
}
function _temp(e) {
  return e.isIntersecting;
}